/**
 * PlantEvidencePanel — public, safe-metadata-only view of a plant's
 * compliance evidence. NO raw files, NO reviewer identity, masked
 * certificate numbers. Authenticated users may request access to the
 * underlying document (purpose required; admin approves with scope +
 * expiration; every access is logged server-side).
 */
import React, { useEffect, useState } from "react";
import { ShieldCheck, FileText, Lock, X } from "lucide-react";
import api from "../api";

const STATUS_LABELS = {
  en: {
    evidence_requested: "Evidence Requested", under_review: "Under Review",
    current: "Current", expiring: "Expiring", expired: "Expired",
    rejected: "Rejected", revoked: "Revoked", scope_not_confirmed: "Scope Not Confirmed",
  },
  es: {
    evidence_requested: "Evidencia solicitada", under_review: "En revisión",
    current: "Vigente", expiring: "Por vencer", expired: "Vencido",
    rejected: "Rechazado", revoked: "Revocado", scope_not_confirmed: "Alcance no confirmado",
  },
};
const STATUS_COLORS = {
  current: { bg: "#dcfce7", color: "#166534" },
  expiring: { bg: "#fef3c7", color: "#92400e" },
  expired: { bg: "#fee2e2", color: "#991b1b" },
  rejected: { bg: "#fee2e2", color: "#991b1b" },
  revoked: { bg: "#fecaca", color: "#7f1d1d" },
  under_review: { bg: "#dbeafe", color: "#1e40af" },
  evidence_requested: { bg: "#f1f5f9", color: "#334155" },
  scope_not_confirmed: { bg: "#ffedd5", color: "#9a3412" },
};

export function PlantEvidencePanel({ slug, lang = "en" }) {
  const [data, setData] = useState(null);
  const [requestDoc, setRequestDoc] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/public/plants/${slug}/evidence`);
        setData(d);
      } catch { /* panel simply doesn't render */ }
    })();
  }, [slug]);

  if (!data) return null;
  const items = data.evidence || [];
  const cv = data.plant?.corridor_verified === true;
  if (!cv && items.length === 0) return null;

  const L = STATUS_LABELS[lang === "es" ? "es" : "en"];

  return (
    <div className="cd-card p-6 space-y-3" data-testid="public-evidence-panel">
      <div className="flex items-center justify-between">
        <div className="overline">{lang === "es" ? "EVIDENCIA DE CUMPLIMIENTO" : "COMPLIANCE EVIDENCE"}</div>
        {cv ? (
          <span
            className="inline-flex items-center gap-1 font-mono"
            style={{ fontSize: 10, padding: "3px 7px", background: "#dcfce7", color: "#166534", border: "1px solid #86efac", borderRadius: 2 }}
            data-testid="public-corridor-verified"
          >
            <ShieldCheck size={10} strokeWidth={2} /> Corridor Verified
            {data.plant.corridor_verified_date ? ` · ${data.plant.corridor_verified_date}` : ""}
          </span>
        ) : null}
      </div>
      <p className="font-mono text-xs" style={{ color: "var(--ink-2)", fontSize: 10 }}>
        {lang === "es"
          ? "Solo metadatos seguros. Los documentos originales son privados — el acceso requiere solicitud aprobada."
          : "Safe metadata only. Raw documents are private — access requires an approved request."}
      </p>
      {items.map((e) => {
        const sc = STATUS_COLORS[e.status] || { bg: "#f8fafc", color: "#64748b" };
        return (
          <div key={e.doc_id} className="border-t pt-3" style={{ borderColor: "var(--line)" }} data-testid={`evidence-item-${e.doc_id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <FileText size={12} strokeWidth={1.6} style={{ color: "var(--ink-2)" }} />
              <strong className="text-sm">{e.document_type}</strong>
              <span className="font-mono" style={{ fontSize: 10, padding: "2px 6px", background: sc.bg, color: sc.color, borderRadius: 2 }} data-testid={`evidence-status-${e.doc_id}`}>
                {L[e.status] || e.status}
              </span>
              <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>v{e.version}</span>
            </div>
            <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>
              {e.plant_sequence_label ? `${e.plant_sequence_label} · ` : ""}
              {e.issuer ? `${e.issuer} · ` : ""}
              {e.certificate_number_masked ? `# ${e.certificate_number_masked}` : ""}
            </div>
            {e.scope ? (
              <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }} data-testid={`evidence-scope-${e.doc_id}`}>
                {lang === "es" ? "Alcance" : "Scope"}: {e.scope}
              </div>
            ) : null}
            <div className="font-mono text-xs mt-1" data-testid={`evidence-dates-${e.doc_id}`}>
              {e.issue_date ? `${lang === "es" ? "Emitido" : "Issued"}: ${e.issue_date}` : ""}
              {e.expiration_date ? ` · ${lang === "es" ? "Vence" : "Expires"}: ${e.expiration_date}` : ""}
            </div>
            <button
              type="button"
              onClick={() => setRequestDoc(e)}
              className="mt-2 font-mono text-xs inline-flex items-center gap-1"
              style={{ border: "1px solid var(--line)", padding: "4px 9px", borderRadius: 2, cursor: "pointer", background: "transparent" }}
              data-testid={`evidence-request-access-${e.doc_id}`}
            >
              <Lock size={10} /> {lang === "es" ? "Solicitar acceso" : "Request access"}
            </button>
          </div>
        );
      })}
      {requestDoc ? (
        <AccessRequestModal doc={requestDoc} lang={lang} onClose={() => setRequestDoc(null)} />
      ) : null}
    </div>
  );
}

function AccessRequestModal({ doc, lang, onClose }) {
  const [purpose, setPurpose] = useState("");
  const [state, setState] = useState("form"); // form | done | login | error
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/vault/documents/${doc.doc_id}/access-request`, { purpose });
      setState(data.already_existed ? "done" : "done");
    } catch (e2) {
      if (e2.response?.status === 401) setState("login");
      else { setState("error"); setErr(String(e2.response?.data?.detail || e2.message)); }
    } finally { setBusy(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 220 }} onClick={onClose} data-testid="evidence-access-overlay">
      <div className="cd-card" style={{ maxWidth: 440, margin: "15vh auto", background: "white", padding: 22, position: "relative" }} onClick={(e) => e.stopPropagation()} data-testid="evidence-access-modal">
        <button type="button" onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 10, right: 10 }} data-testid="evidence-access-close">
          <X size={15} />
        </button>
        <div className="overline">{lang === "es" ? "SOLICITAR ACCESO AL DOCUMENTO" : "REQUEST DOCUMENT ACCESS"}</div>
        <div className="font-mono text-xs mt-2" style={{ color: "var(--ink-2)" }}>{doc.document_type} · v{doc.version}</div>
        {state === "form" ? (
          <form onSubmit={submit} className="mt-3">
            <label className="overline">{lang === "es" ? "Propósito (requerido)" : "Purpose (required)"}</label>
            <textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} rows={3} required className="cd-input mt-2 w-full font-mono text-xs" placeholder={lang === "es" ? "¿Por qué necesitas ver este documento?" : "Why do you need to see this document?"} data-testid="evidence-purpose-input" />
            <button type="submit" disabled={busy || !purpose.trim()} className="btn btn-primary w-full mt-3" data-testid="evidence-request-submit">
              {lang === "es" ? "Enviar solicitud" : "Submit request"}
            </button>
          </form>
        ) : state === "done" ? (
          <div className="mt-3 font-mono text-xs" data-testid="evidence-request-success">
            {lang === "es"
              ? "Solicitud enviada. El equipo de Corridor la revisará; el acceso aprobado tiene alcance y fecha de expiración."
              : "Request submitted. The Corridor team will review it; approved access carries a scope and an expiration."}
          </div>
        ) : state === "login" ? (
          <div className="mt-3 font-mono text-xs" data-testid="evidence-request-login">
            {lang === "es"
              ? "Necesitas una cuenta para solicitar acceso. "
              : "You need an account to request access. "}
            <a href="/login" className="underline">{lang === "es" ? "Iniciar sesión" : "Sign in"}</a>
          </div>
        ) : (
          <div className="mt-3 font-mono text-xs" style={{ color: "#dc2626" }} data-testid="evidence-request-error">{err}</div>
        )}
      </div>
    </div>
  );
}
